import type { CSSProperties, ReactNode } from 'react';
import { S, getThemeVars } from '../../config/theme.js';
import Icon from '../ui/Icon';
import { DrpxLogo, DrpxLogoTopbar } from '../logo/DrpxLogo';
import { CookieBanner } from '../pwa/CookieBanner';
import { OfflineIndicator } from '../pwa/OfflineIndicator';
import { FloatingActionButton } from '../pwa/FloatingActionButton';
import { PullToRefresh } from '../pwa/PullToRefresh';
import { LiveBanner } from '../pwa/LiveBanner';
import { PwaInstallBanner } from '../pwa/PwaInstallBanner';
import { OnboardingTour } from '../ui/OnboardingTour.jsx';
import { Toast } from '../ui/index';
import { ModalRenderer } from './ModalRenderer';
import { useApp } from '../../contexts/AppContext';
import { renderNotifDropdown } from './renderNotifDropdown';
import { renderLangDropdown } from './renderLangDropdown';
import { renderMobileDrawer } from './renderMobileDrawer';
import { renderBottomNav } from './renderBottomNav';
import { renderBottomSheet } from './renderBottomSheet';
import { themeModeIcon, themeButtonTitle } from '../../lib/theme';
import { rollenLabelKey, istFirmenZugang } from '../../lib/zugang.js';

interface AppShellProps {
  children: ReactNode;
}

/**
 * AppShell — Hauptlayout der App nach dem Login.
 * Enthält Topbar, Notification-Panel, Mobile-Drawer, Bottom-Nav,
 * Page-Content, Modals und Overlays.
 *
 * Nutzt `useApp()` Context — keine Props außer `children`.
 */
export function AppShell({ children }: AppShellProps) {
  const ctx = useApp();
  const {
    user,
    users,
    setAuftraege,
    tab,
    onNav: nav,
    navTabs,
    navBadges,
    mobileMenu,
    setMobileMenu,
    bottomSheet,
    setBottomSheet,
    notifOpen,
    setNotifOpen,
    unreadCount,
    pushNotif,
    liveBanner,
    setLiveBanner,
    darkMode,
    themeMode,
    cycleThemeMode,
    lang,
    langOpen,
    setLangOpen,
    LANG_LABELS,
    LANG_FLAGS,
    cookieConsent,
    handleCookieConsent,
    onboardingStep,
    setOnboardingStep,
    finishOnboarding,
    logout,
    t,
    toast,
    setToast,
    modal,
    setModal,
    onOpenModal: openModal,
    openToast,
    ladeChatUngelesen,
    chatUngelesen,
  } = ctx;

  if (!user) return null;

  // Helper-Funktionen erhalten ctx als Argument (enthält alle Context-Werte).
  // `user` wird explizit überschrieben (nach Narrowing nicht mehr nullable).
  const props = { ...ctx, user, nav: ctx.onNav, openModal: ctx.onOpenModal };

  // ─── Topbar ──────────────────────────────────────────────────────────────────
  const topbar = (
    <header
      style={{
        background: 'linear-gradient(135deg,#152a1a,#1e3824,#1a3020)',
        boxShadow: '0 2px 20px rgba(0,0,0,.25)',
        position: 'sticky' as const,
        top: 0,
        zIndex: 101,
      }}>
      <div
        className="topbar-row"
        style={{ ...(S.rowBetween as CSSProperties), padding: '0 20px', height: 52, gap: 10 }}>
        {/* Logo */}
        <div className="logo-wrap flex-row-8-shrink">
          <DrpxLogoTopbar size={0.84} dark={true} />
        </div>
        {/* Actions */}
        <div className="topbar-actions flex-row-8-shrink">
          {/* Chat Inbox Button */}
          {user.rolle !== 'admin' &&
            <button
              onClick={function () { openModal('chatInbox'); }}
              aria-label={t('chatInboxTitle')}
              style={{
                position: 'relative' as const,
                cursor: 'pointer',
                background: 'rgba(255,255,255,.15)',
                border: '1px solid rgba(255,255,255,.25)',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 15,
                color: 'rgba(255,255,255,.85)',
                lineHeight: 1,
              }}>
              <Icon name="message" size={16} />
              {Object.keys(chatUngelesen || {}).length > 0 &&
                <span
                  style={{
                    position: 'absolute' as const,
                    top: -4,
                    right: -4,
                    width: 16,
                    height: 16,
                    background: '#ef4444',
                    borderRadius: '50%',
                    border: '2px solid #2d4a32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 9,
                    fontWeight: 800,
                    color: '#fff',
                  }}>
                  {Object.keys(chatUngelesen).length}
                </span>}
            </button>}
          {/* Notification Bell */}
          <div className="pos-rel">
            <button
              onClick={function () {
                setNotifOpen(function (o) {
                  return !o;
                });
              }}
              style={{
                position: 'relative' as const,
                cursor: 'pointer',
                background: 'rgba(255,255,255,.15)',
                border: '1px solid rgba(255,255,255,.25)',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 15,
                color: 'rgba(255,255,255,.85)',
                lineHeight: 1,
              }}>
              <Icon name="bell" size={16} />
              {unreadCount > 0 &&
                <span
                  style={{
                    position: 'absolute' as const,
                    top: -4,
                    right: -4,
                    width: 16,
                    height: 16,
                    background: '#ef4444',
                    borderRadius: '50%',
                    border: '2px solid #2d4a32',
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>}
            </button>
            {/* Notification Dropdown */}
            {notifOpen && renderNotifDropdown(props)}
          </div>
          {/* Language Switcher */}
          <div className="topbar-lang pos-rel">
            <button
              onClick={function () {
                setLangOpen(function (o) {
                  return !o;
                });
              }}
              style={{
                ...(S.row as CSSProperties),
                gap: 4,
                background: 'rgba(255,255,255,.15)',
                border: '1px solid rgba(255,255,255,.25)',
                color: '#fff',
                borderRadius: 8,
                padding: '4px 10px',
                fontSize: 11,
                cursor: 'pointer',
                fontWeight: 700,
              }}>
              {LANG_FLAGS[lang]}
              {' '}
              {LANG_LABELS[lang]}
              {' '}
              <span style={{ fontSize: 8, opacity: 0.5 }}>
                ▼
              </span>
            </button>
            {langOpen && renderLangDropdown(props)}
          </div>
          {/* Darstellung: Auto → Hell → Dunkel (10.10.2026). Das Symbol
              zeigt die AKTUELLE Stufe (vorher: die, zu der man wechselt). */}
          <button
            className="topbar-darkmode"
            onClick={cycleThemeMode}
            title={themeButtonTitle(themeMode, t)}
            aria-label={themeButtonTitle(themeMode, t)}
            style={{
              background: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.25)',
              color: 'rgba(255,255,255,.85)',
              borderRadius: 8,
              padding: '4px 9px',
              fontSize: 14,
              cursor: 'pointer',
              lineHeight: 1,
            }}>
            <Icon
              name={themeModeIcon(themeMode)}
              size={16}
              color={themeMode === 'light' ? '#fbbf24' : themeMode === 'dark' ? '#cbd5e1' : 'rgba(255,255,255,.85)'} />
          </button>
          {/* User Badge */}
          <div
            className="topbar-userbadge"
            style={{
              background: 'rgba(255,255,255,.18)',
              border: '1px solid rgba(255,255,255,.30)',
              borderRadius: 20,
              padding: '4px 12px',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#fff',
              whiteSpace: 'nowrap' as const,
            }}>
            <div
              style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e' }} />
            <span>
              {user.firma || user.name}
            </span>
            <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 10 }}>
              {rollenLabelKey(user) ? t(rollenLabelKey(user) as string) : 'Admin'}
            </span>
          </div>
          {/* Logout */}
          <button
            className="topbar-logout"
            onClick={logout}
            style={{
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.28)',
              color: 'rgba(255,255,255,.85)',
              borderRadius: 8,
              padding: '4px 10px',
              fontSize: 11,
              cursor: 'pointer',
              whiteSpace: 'nowrap' as const,
            }}>
            {t('abmelden')}
          </button>
          {/* Mobile Hamburger */}
          <button
            className="mobile-hamburger"
            onClick={function () {
              setMobileMenu(function (m) {
                return !m;
              });
            }}
            style={{
              display: 'none',
              background: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.25)',
              borderRadius: 10,
              padding: '8px 11px',
              fontSize: 18,
              color: '#fff',
              cursor: 'pointer',
              lineHeight: 1,
            }}>
            <Icon name={mobileMenu ? 'cross' : 'menu'} size={18} />
          </button>
        </div>
      </div>
      {/* Nav Tabs Row */}
      <div
        className="nav-tabs-wrap"
        style={{
          borderTop: '1px solid rgba(255,255,255,.06)',
          padding: '0 16px',
          overflowX: 'auto' as const,
          WebkitOverflowScrolling: 'touch' as const,
          scrollbarWidth: 'none' as const,
          msOverflowStyle: 'none' as const,
        }}>
        <div
          className="nav-tabs"
          style={{ ...(S.row as CSSProperties), gap: 1, padding: '6px 0', minWidth: 'max-content', flexWrap: 'nowrap' as const }}>
          {navTabs.map(function (nt, i) {
            return nt.divider
              ? <div
                  key={'d' + i}
                  style={{ ...(S.row as CSSProperties), gap: 6, padding: '4px 8px', flexShrink: 0 }}>
                  <div
                    style={{ width: 1, height: 24, background: 'rgba(255,255,255,.15)', borderRadius: 1 }} />
                  <span
                    style={{
                      fontSize: 9,
                      color: 'rgba(255,255,255,.5)',
                      fontWeight: 700,
                      textTransform: 'uppercase' as const,
                      letterSpacing: 1,
                      whiteSpace: 'nowrap' as const,
                      background: 'rgba(255,255,255,.06)',
                      padding: '3px 8px',
                      borderRadius: 6,
                      border: '1px solid rgba(255,255,255,.08)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}>
                    {nt.divIcon && <Icon name={nt.divIcon} size={10} color="rgba(255,255,255,.5)" />}
                    {nt.label}
                    {nt.locked && <Icon name="lock" size={9} color="rgba(255,255,255,.4)" />}
                  </span>
                </div>
              : <button
                  key={nt.id}
                  onClick={function () {
                    nav(nt.id);
                  }}
                  style={{
                    padding: '6px 10px',
                    border: 'none',
                    background: tab === nt.id ? 'rgba(255,255,255,.12)' : 'transparent',
                    color: tab === nt.id ? '#fff' : 'rgba(255,255,255,.6)',
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: 12,
                    cursor: 'pointer',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap' as const,
                    flexShrink: 0,
                    fontWeight: tab === nt.id ? 600 : 500,
                    transition: 'all .15s',
                  }}>
                  <Icon name={nt.icon} size={12} />
                  {' '}
                  {nt.label}
                  {navBadges[nt.id] > 0 &&
                    <span
                      style={{
                        background: nt.id === 'admin-users' ? '#f59e0b' : '#ef4444',
                        color: '#fff',
                        borderRadius: 20,
                        padding: '0 5px',
                        fontSize: 9,
                        fontWeight: 700,
                        minWidth: 16,
                        textAlign: 'center' as const,
                        lineHeight: '16px',
                      }}>
                      {navBadges[nt.id] > 99 ? '99+' : navBadges[nt.id]}
                    </span>}
                  {nt.locked && <Icon name="lock" size={10} color="rgba(255,255,255,.3)" />}
                </button>;
          })}
        </div>
      </div>
    </header>
  );

  // ─── Main Return ─────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: "'DM Sans',sans-serif",
        background: 'var(--bg)',
        color: 'var(--text)',
        minHeight: '100vh',
        overflowX: 'hidden',
        backgroundImage: 'radial-gradient(circle at 1px 1px, var(--border) .3px, transparent 0)',
        backgroundSize: '32px 32px',
        ...getThemeVars(darkMode, false),
      }}>
      {topbar}
      {/* Mobile Menu Overlay */}
      {mobileMenu &&
        <div
          onClick={function () {
            setMobileMenu(false);
          }}
          style={{
            position: 'fixed' as const,
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 200,
            animation: 'fadeIn .2s ease',
          }} />}
      {/* Mobile Drawer */}
      {mobileMenu && renderMobileDrawer(props)}
      {/* Notif close overlay */}
      {notifOpen &&
        <div
          onClick={function () {
            setNotifOpen(false);
          }}
          style={{ position: 'fixed' as const, inset: 0, zIndex: 100 }} />}
      {/* Page Content */}
      <PullToRefresh>
        {children}
      </PullToRefresh>
      {/* Modals */}
      <ModalRenderer
        modal={modal}
        setModal={setModal}
        user={user}
        users={users}
        setAuftraege={setAuftraege}
        openToast={openToast}
        pushNotif={pushNotif}
        ladeChatUngelesen={ladeChatUngelesen}
        openModal={openModal} />
      {/* Onboarding */}
      {onboardingStep >= 0 &&
        user &&
        user.rolle !== 'admin' &&
        <OnboardingTour
          step={onboardingStep}
          setStep={setOnboardingStep}
          onFinish={finishOnboarding}
          rolle={istFirmenZugang(user) ? 'firma' : user.rolle} />}
      {/* Toast */}
      {toast &&
        <Toast
          key={toast.id}
          message={toast.msg}
          type={toast.type}
          onDone={function () {
            setToast(null);
          }} />}
      {/* Overlays */}
      <OfflineIndicator />
      <FloatingActionButton user={user} onNav={nav} onOpenModal={openModal} />
      {liveBanner &&
        <LiveBanner
          banner={liveBanner}
          onClose={function () {
            setLiveBanner(null);
          }}
          onAction={function () {
            if (liveBanner.goToTab) nav(liveBanner.goToTab);
          }} />}
      {user && <PwaInstallBanner />}
      {/* Cookie Banner */}
      {!cookieConsent && <CookieBanner onAccept={handleCookieConsent} />}
      {/* Bottom Nav */}
      {renderBottomNav(props)}
      {/* Bottom Sheet Overlay */}
      {bottomSheet &&
        <div
          onClick={function () {
            setBottomSheet(false);
          }}
          style={{
            position: 'fixed' as const,
            inset: 0,
            background: 'rgba(0,0,0,.4)',
            backdropFilter: 'blur(3px)',
            zIndex: 200,
            animation: 'fadeIn .15s ease',
          }} />}
      {/* Bottom Sheet */}
      {bottomSheet && renderBottomSheet(props)}
    </div>
  );
}

// `DrpxLogo` wird nur referenziert, wenn AppShell nicht gerendert wird (Gate-Screens).
// Re-Export für konsumierende Caller, damit der Import-Hook weiter funktioniert.
export { DrpxLogo };
